import { forwardRef, MutableRefObject, useEffect, useImperativeHandle, useRef } from 'react';
import { GAME_MODE, PlayerSetup, Song } from '~/interfaces';
import { VideoPlayerRef, VideoState } from '~/modules/Elements/VideoPlayer';
import RoomStateStore from '~/modules/Multiplayer/RoomState';
import { playerColor } from '~/modules/Multiplayer/playerColors';
import ScoreReporter from '~/modules/Multiplayer/ScoreReporter';
import PlayersManager from '~/modules/Players/PlayersManager';
import MultiplayerLeaderboard from '~/routes/Game/Singing/GameOverlay/Components/MultiplayerLeaderboard';
import SkipIntro from '~/routes/Game/Singing/GameOverlay/Components/SkipIntro';
import SkipOutro from '~/routes/Game/Singing/GameOverlay/Components/SkipOutro';
import { GraphicSetting, MobilePhoneModeSetting, useSettingValue } from '~/routes/Settings/SettingsState';
import GameState from '../../../../modules/GameEngine/GameState/GameState';
import DurationBar from './Components/DurationBar';
import Lyrics from './Components/Lyrics';
import ScoreText from './Components/ScoreText';

import CanvasDrawing from '~/modules/GameEngine/Drawing';
import fragShader from '~/modules/GameEngine/Drawing/Shaders/shader.frag?raw';
import vertShader from '~/modules/GameEngine/Drawing/Shaders/shader.vert?raw';
import tuple from '~/modules/utils/tuple';
import getPlayerScoreData from '~/routes/Game/Singing/GameOverlay/helpers/getPlayerScoreData';

interface Props {
  song: Song;
  currentStatus: VideoState;
  width: number;
  height: number;
  onSongEnd?: () => void;
  playerSetups: PlayerSetup[];
  duration: number;
  effectsEnabled: boolean;
  videoPlayerRef: MutableRefObject<VideoPlayerRef | null>;
  isPauseMenuVisible: boolean;
}

const MAX_RENDER_RESOLUTION_W = 1920;

const GameOverlay = forwardRef(function (
  {
    currentStatus,
    width,
    height,
    playerSetups,
    onSongEnd,
    effectsEnabled,
    videoPlayerRef,
    isPauseMenuVisible,
    duration,
  }: Props,
  fRef,
) {
  const [graphicLevel] = useSettingValue(GraphicSetting);
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const drawer = useRef<CanvasDrawing | null>(null);
  const lyrics = useRef<HTMLDivElement | null>(null);

  const overlayWidth = MAX_RENDER_RESOLUTION_W;
  const overlayHeight = overlayWidth * (height / width);

  const overlayScaleFactor = overlayHeight / height;

  useImperativeHandle(fRef, () => {
    return {
      pause: () => drawer.current?.pause(),
      resume: () => drawer.current?.resume(),
    };
  }, []);

  const isMultiplayer = GameState.getSingSetup()?.mode === GAME_MODE.MULTIPLAYER || RoomStateStore.isInRoom;

  useEffect(() => {
    GameState.startInputMonitoring();

    // Start multiplayer score reporting
    if (isMultiplayer) {
      ScoreReporter.start(() => GameState.getPlayerScore(0));
    }

    return () => {
      GameState.stopInputMonitoring();
      ScoreReporter.stop();
    };
  }, []);

  useEffect(() => {
    if (!canvas.current || !lyrics.current) return;

    drawer.current = new CanvasDrawing(canvas.current, lyrics.current.offsetHeight * overlayScaleFactor);
    drawer.current.start();

    return () => {
      drawer.current?.end();
    };
  }, [overlayScaleFactor]);

  useEffect(() => {
    if (isPauseMenuVisible && drawer.current?.isPlaying()) {
      drawer.current?.pause();
    } else if (!isPauseMenuVisible && !drawer.current?.isPlaying()) {
      drawer.current?.resume();
    }
  }, [isPauseMenuVisible]);

  useEffect(() => {
    if (currentStatus === VideoState.ENDED) {
      onSongEnd?.();
    }
  }, [currentStatus, onSongEnd]);

  const players = PlayersManager.getPlayers();
  const showMultipleLines = !mobilePhoneMode && players.length > 1;

  return (
    <div className="relative flex h-full flex-col font-bold text-white">
      {graphicLevel === 'high' && (
        <>
          <script type={'x-shader/x-fragment'} id={'plane-fs'}>
            {fragShader}
          </script>
          <script id="plane-vs" type="x-shader/x-vertex">
            {vertShader}
          </script>
          <div id="canvas" className="absolute inset-0 z-[10000] h-full w-full" />
        </>
      )}
      <div id="plane" className="absolute inset-0 h-full w-full" style={!effectsEnabled ? { opacity: 0 } : undefined}>
        <canvas
          ref={canvas}
          width={overlayWidth}
          height={overlayHeight}
          data-sampler="planeTexture"
          className="h-full w-full"
        />
      </div>
      {effectsEnabled && (
        <>
          <SkipIntro playerRef={videoPlayerRef} isEnabled={!isPauseMenuVisible} />
          <SkipOutro onSongEnd={onSongEnd} isEnabled={!isPauseMenuVisible} />
        </>
      )}
      {isMultiplayer && <MultiplayerLeaderboard />}
      <DurationBar players={playerSetups} currentStatus={currentStatus} duration={duration} />
      <div className="py-5">
        {showMultipleLines && (
          <Lyrics player={players[0]} effectsEnabled={effectsEnabled} showStatusForAllPlayers={players.length > 2} />
        )}
      </div>
      <div className="mobile:text-xl z-10 flex h-full flex-1 flex-col justify-around pr-4 text-right text-3xl">
        {effectsEnabled && GameState.getSingSetup() && (
          <>
            {isMultiplayer ? (
              <span
                className="flex flex-col items-end"
                data-test="players-score"
                data-score={Math.floor(GameState.getPlayerScore(0))}>
                {/* whose score this is — in a room everyone sees only their own big number */}
                <span
                  className="mobile:text-xs max-w-64 truncate text-base font-bold tracking-wide"
                  style={{ color: playerColor(RoomStateStore.myPlayer?.color).hex }}>
                  {RoomStateStore.myPlayer?.name ?? 'Ty'}
                </span>
                <ScoreText score={GameState.getPlayerScore(0)} />
              </span>
            ) : GameState.getSingSetup()?.mode === GAME_MODE.CO_OP ? (
              <span data-test="players-score" data-score={Math.floor(GameState.getPlayerScore(0))}>
                <ScoreText score={GameState.getPlayerScore(0)} />
              </span>
            ) : (
              PlayersManager.getPlayers().map((player) => {
                const scores = PlayersManager.getPlayers().map((player) =>
                  tuple([player.number, GameState.getPlayerScore(player.number)]),
                );
                const { score, isFirst } = getPlayerScoreData(scores, player.number);

                return (
                  <span
                    className="typography relative"
                    key={player.number}
                    data-test={`player-${player.number}-score`}
                    data-score={Math.floor(score)}>
                    <ScoreText score={score} />
                    <div
                      className={`mobile:top-6 mobile:text-lg absolute top-10 rotate-12 text-xl transition-all duration-200 ${isFirst ? '-right-2' : '-right-36'}`}>
                      {isFirst ? <div className="motion-preset-pulse-sm opacity-75">🥇</div> : '🥇'}
                    </div>
                  </span>
                );
              })
            )}
          </>
        )}
      </div>
      <div className="py-5" ref={lyrics}>
        <Lyrics
          showStatusForAllPlayers={players.length > 2}
          player={players.length > 2 ? players[0] : players.at(-1)!}
          bottom
          effectsEnabled={effectsEnabled}
        />
      </div>
    </div>
  );
});

export default GameOverlay;
